import {
  Component,
  OnInit,
  Input,
  EventEmitter,
  Output,
  ChangeDetectorRef,
} from '@angular/core';
import { NgxImageCompressService, DOC_ORIENTATION } from 'ngx-image-compress';
import { Subject } from 'rxjs';
import { ModalService } from 'src/app/app-modals/services/modal.service';
import { DPUploadType } from 'src/app/core/models/enums/DPUploadType';
import { UtilsService } from 'src/app/core/services/utils.service';

interface IResolveItem {
  name: string;
  type: string;
  data?: string;
  size?: number;
}

const SIZE_LIMIT = 20 * 1024 * 1024;

declare var Tiff: any;

@Component({
  selector: 'app-file-upload-modal',
  templateUrl: './file-upload-modal.component.html',
  styleUrls: ['./file-upload-modal.component.scss'],
})
export class FileUploadModalComponent implements OnInit {
  // Upload Type
  @Input() uploadType: DPUploadType;

  @Input() title: string;

  @Input() status?: string;

  @Input() complianceInfo?: string[];

  @Input() instructions?: string;

  @Input() rejectReason?: string;

  @Input() events?: Subject<{ type: string; data?: any }>;

  // Close event emitter
  @Output() dismiss = new EventEmitter();

  // Submit event emitter
  @Output() submit = new EventEmitter();

  // Resolve items
  public items: IResolveItem[] = [];

  public loading = false;

  constructor(
    public utils: UtilsService,
    private cdr: ChangeDetectorRef,
    private imageCompress: NgxImageCompressService,
    private appModal: ModalService
  ) {}

  ngOnInit(): void {
    this.initItems();
    this.events?.subscribe(({ type, data }) => {
      switch (type) {
        case 'loading':
          this.loading = data;
          break;
        case 'close':
          this.dismiss.emit();
          break;
        case 'error':

          break;
        default:
          break;
      }
    });
    // this.checkCameraPermission();
  }

  /**
   * Submit result
   */
  submitForm() {
    // Guard against double-submit (double-click, enter key, duplicate events)
    if (this.loading) return;

    // If the files exceed limit, show alert.
    if (this.isOverLimit()) {
      this.appModal.showAlert({
        type: 'info',
        text: 'File is too large. Please reduce the size of the file.',
      });
      return;
    }

    this.loading = true;
    this.submit.emit(this.items.map((item) => item.data));
  }

  /**
   * Event handler to select files to upload
   * @param index Index in items array
   */
  uploadFile(index?: number) {
    let selectedFiles: File[] = [];

    /**
     * Select file and check size limit and file types.
     */
    this.selectFile(this.allowNewFiles)
      .then((files: File[]) => {
        selectedFiles = files;

        return Promise.all(
          files.map((f) =>
            this.readFile(f).then(this.processImage.bind(this, f))
          )
        );
      })
      .then((data: string[]) => {
        this.updateItems(selectedFiles, data, index);
      })
      .catch((err) => {
        let error: string;
        switch (err) {
          case 'size_limit':
            error = 'File is too large. Please reduce the size of the file.';
            break;
          case 'invalid_type':
            error = 'Invalid File Type. Please select JPG, PNG, TIF or PDF.';
            break;
          default:
            error = 'Unknown error occurred.';
            break;
        }

        this.appModal.showAlert({
          type: 'danger',
          text: error,
        });
      });
  }

  /**
   * Remove an item to upload.
   * @param index Index in items array
   */
  public removeFile(index: number) {
    if (this.allowNewFiles) {
      this.items = this.items.filter((_, i) => i !== index);
    } else {
      this.items[index].data = null;
      this.items[index].name = index === 0 ? 'Front' : 'Back';
      this.items[index].size = -1;
    }
  }

  /**
   * Rotate image item using Canvas APIs.
   * @param i Index in items array
   */
  public async rotateImage(i: number) {
    const item = this.items[i];

    const { width, height, image } = await this.getImageInfo(item.data);

    const canvas = document.createElement('canvas');
    canvas.width = height;
    canvas.height = width;
    const ctx = canvas.getContext('2d');

    ctx.save();
    ctx.translate(height, 0);
    ctx.rotate(Math.PI / 2);
    ctx.drawImage(image, 0, 0, width, height);
    ctx.restore();

    item.data = canvas.toDataURL();
  }

  /**
   * Whether to enable submit button.
   *
   * The submit button is enabled when there are valid items with data.
   */
  public get canSubmit() {
    return (
      this.items.length > 0 &&
      this.items.every((item) => !!item.data) &&
      !this.loading
    );
  }

  public get allowNewFiles() {
    return this.uploadType === DPUploadType.MultiFiles;
  }

  /**
   * Text message for placeholder card to add new item.
   */
  public get placeholderMessage() {
    return this.allowNewFiles && this.items.length > 0
      ? 'Add Another Page'
      : 'Upload or Take Photo';
  }

  /**
   * Initialize items array based on task type.
   */
  private initItems() {
    switch (this.uploadType) {
      case DPUploadType.DriverLicense:
        this.items = [
          { name: 'Front', type: 'image' },
          { name: 'Back', type: 'image' },
        ];
        break;
      case DPUploadType.MultiFiles:
        this.items = [];
        break;
      case DPUploadType.SingleFile:
        this.items = [{ name: 'Front', type: 'image' }];
        break;
    }
  }

  /**
   * Select files to upload
   *
   * @param multiple Whether to select multiple files or not
   * @returns {Promise} Promise which resolves selected file(s).
   */
  private selectFile(multiple?: boolean) {
    return new Promise((resolve, reject) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.multiple = multiple;
      input.accept = 'image/jpeg,image/png,image/tiff,application/pdf';
      input.className = 'hidden-file-input';
      document.body.appendChild(input);

      input.addEventListener('change', () => {
        const files: File[] = [];
        for (let i = 0; i < input.files.length; i++) {
          if (this.utils.isAllowedFile(input.files[i].name)) {
            files.push(input.files[i]);
          } else {
            reject('invalid_type');
          }
        }
        resolve(files);
      });

      input.click();
    });
  }

  /**
   * Read file content.
   *
   * Reading Tiff content as base64 is not supported by browsers.
   * An external library must be used to get base64 data.
   *
   * @param file File object
   * @returns {Promise} Promise to resolve file content in base64 format.
   */
  private readFile(file: File): Promise<string> {
    return new Promise((resolve) => {
      const isTiff = /\.tiff?$/i.test(file.name);
      const reader = new FileReader();
      reader.addEventListener('load', (event) => {
        if (isTiff) {
          const tiff = new Tiff({ buffer: event.target.result as ArrayBuffer });
          resolve(tiff.toCanvas().toDataURL());
        } else {
          resolve(event.target.result as string);
        }
      });
      if (isTiff) {
        reader.readAsArrayBuffer(file);
      } else {
        reader.readAsDataURL(file);
      }
    });
  }

  /**
   * Get image info such as width, height, zoom level and Image object.
   * @param data Base64 data string
   * @returns {Promise} Promise which resolves image info
   */
  private getImageInfo(data: string): Promise<{
    zoom: number;
    width: number;
    height: number;
    image: HTMLImageElement;
  }> {
    return new Promise((resolve, reject) => {
      const sourceImage = new Image();
      sourceImage.onload = function () {
        let w, h;
        w = sourceImage.naturalWidth;
        h = sourceImage.naturalHeight;
        let zoom = 1;

        if (w > h && w > 1500) {
          // If the image is wide and the width is more than 1500 pixel,
          // it should be scaled down to fit within 1500 pixels.
          zoom = 1500 / w;
        } else if (w < h && h > 1500) {
          // If the image is narrow and the height is more than 1500 pixel,
          // it should be scaled down to fit within 1500 pixels.
          zoom = 1500 / h;
        }

        resolve({
          zoom: zoom * 100,
          width: w,
          height: h,
          image: sourceImage,
        });
      };
      sourceImage.onerror = function (err) {
        reject(err);
      };
      sourceImage.src = data;
    });
  }

  /**
   * Compress image if it's larger than 1500 pixels in width or height.
   *
   * @param file
   * @param data
   * @returns
   */
  private async processImage(file: File, data: string): Promise<string> {
    if (!this.utils.isImage(file.name)) {
      return data;
    }

    const { zoom } = await this.getImageInfo(data);
    const result = await this.imageCompress.compressFile(
      data,
      DOC_ORIENTATION.Up,
      zoom,
      100
    );
    return result;
  }

  /**
   * Checks if total size of items is over limit.
   * @returns {boolean} Whether total item size is over limit
   */
  private isOverLimit() {
    const totalSize = this.items.reduce((sum, item) => sum + item.size, 0);
    return totalSize > SIZE_LIMIT;
  }

  /**
   * Helper function to update items array.
   *
   * @param files Files to update
   * @param data Base64 string of files
   * @param index Index to replace. If undefined, insert at the end of array.
   */
  private updateItems(files: File[], data: string[], index) {
    if (index === undefined) {
      files.forEach((file, i) => {
        this.items.push({
          name: file.name,
          type: this.utils.getExtension(file.name),
          data: data[i],
          size: file.size,
        });
      });
    } else {
      this.items[index] = {
        name: files[0].name,
        type: this.utils.getExtension(files[0].name),
        data: data[0],
        size: files[0].size,
      };
    }

    this.cdr.detectChanges();
  }

  /**
   * Check camera permission.
   *
   * @deprecated
   */
  private checkCameraPermission() {
    const isMobile = 'ontouchstart' in document.documentElement;
    if (!isMobile) {
      return;
    }

    const cameraEnabled = localStorage.getItem('cameraEnabled');
    if (cameraEnabled === 'true') {
      return;
    }

    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then((stream) => {
        stream.getVideoTracks().forEach((track) => {
          track.stop();
        });
        localStorage.setItem('cameraEnabled', 'true');
      })
      .catch((err) => {
        if (err.code === 8) {
          this.appModal.showAlert({
            type: 'info',
            text: 'Camera is not found in your device. You can still upload photos.',
          });
        } else if (err.code === 0) {
          this.appModal.showAlert({
            type: 'info',
            text: 'You denied access to your camera. You can still upload photos.',
          });
        } else {
          this.appModal.showAlert({
            type: 'info',
            text: 'Camera is available in your device. You can still upload photos.',
          });
        }
      });
  }
}
